import React, { useState, useEffect, useRef } from 'react';
import {
  collection, query, where, onSnapshot, addDoc,
  doc, setDoc, serverTimestamp, orderBy, limit,
} from 'firebase/firestore';
import { db } from '../../core/firebase/firebase';
import { useAuth } from '../../core/firebase/useAuth';
import { useAuthModal } from '../../core/AuthModalContext';
import { IconMessage, IconSend, IconArrowLeft, IconUser } from '../../components/Icons';

/* ── Conversation list item ── */
function ConvItem({ conv, currentUid, onClick }) {
  const other = (conv.participantNames || {})[
    (conv.participants || []).find(id => id !== currentUid) || ''
  ] || 'Utilisateur';
  const ts = conv.lastTime?.toDate?.();
  const time = ts ? ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <button style={styles.convItem} onClick={onClick}>
      <div style={styles.avatar}>
        <IconUser size={22} color="#8888aa" />
      </div>
      <div style={styles.convInfo}>
        <div style={styles.convRow}>
          <span style={styles.convName}>{other}</span>
          <span style={styles.convTime}>{time}</span>
        </div>
        <span style={styles.convLast}>{conv.lastMessage || '…'}</span>
      </div>
    </button>
  );
}

/* ── Chat view ── */
function ChatView({ conv, currentUid, onBack }) {
  const [text,     setText]     = useState('');
  const [messages, setMessages] = useState([]);
  const [sending,  setSending]  = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(
        collection(db, 'conversations', conv.id, 'messages'),
        orderBy('createdAt', 'asc'),
        limit(100),
      ),
      snap => {
        setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      }
    );
    return unsub;
  }, [conv.id]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    setText('');
    try {
      await addDoc(collection(db, 'conversations', conv.id, 'messages'), {
        text      : t,
        senderId  : currentUid,
        createdAt : serverTimestamp(),
      });
      await setDoc(doc(db, 'conversations', conv.id), {
        lastMessage : t,
        lastTime    : serverTimestamp(),
      }, { merge: true });
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  const otherName = (conv.participantNames || {})[
    (conv.participants || []).find(id => id !== currentUid) || ''
  ] || 'Utilisateur';

  return (
    <div style={styles.chatWrap}>
      {/* Header */}
      <div style={styles.chatHeader}>
        <button style={styles.backBtn} onClick={onBack}>
          <IconArrowLeft size={22} color="#1a1a1a" />
        </button>
        <div style={styles.chatAvatar}>
          <IconUser size={20} color="#8888aa" />
        </div>
        <span style={styles.chatName}>{otherName}</span>
      </div>

      {/* Messages */}
      <div style={styles.msgList}>
        {messages.map(m => {
          const mine = m.senderId === currentUid;
          return (
            <div key={m.id} style={{ ...styles.bubble, ...(mine ? styles.bubbleMine : styles.bubbleOther) }}>
              <span style={{ ...styles.bubbleText, color: mine ? '#fff' : '#1a1a1a' }}>{m.text}</span>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={styles.inputRow}>
        <input
          style={styles.msgInput}
          placeholder="Écrire un message…"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
        />
        <button style={{ ...styles.sendBtn, opacity: text.trim() && !sending ? 1 : 0.4 }} onClick={send}>
          <IconSend size={20} color="#fff" />
        </button>
      </div>
    </div>
  );
}

/* ── Main page ── */
export default function MessagesPage() {
  const { user, loading: authLoading } = useAuth();
  const { showAuthModal } = useAuthModal();
  const [convs,   setConvs]   = useState([]);
  const [active,  setActive]  = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const unsub = onSnapshot(
      query(collection(db, 'conversations'), where('participants', 'array-contains', user.uid)),
      snap => {
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.lastTime?.seconds || 0) - (a.lastTime?.seconds || 0));
        setConvs(list);
        setLoading(false);
      },
      err => { console.error(err); setLoading(false); }
    );
    return unsub;
  }, [user]);

  if (authLoading || loading) {
    return (
      <div style={styles.centered}>
        <div style={styles.spinner} />
      </div>
    );
  }

  /* ── Non connecté ── */
  if (!user) {
    return (
      <div style={styles.centered}>
        <div style={styles.lockBox}>
          <IconMessage size={32} color="#4361ee" />
        </div>
        <h2 style={styles.lockTitle}>Vos messages</h2>
        <p style={styles.lockSub}>Connectez-vous pour voir et envoyer des messages.</p>
        <button style={styles.btnPrimary} onClick={showAuthModal}>
          Se connecter
        </button>
      </div>
    );
  }

  /* ── Chat ouvert ── */
  if (active) {
    return <ChatView conv={active} currentUid={user.uid} onBack={() => setActive(null)} />;
  }

  /* ── Liste conversations ── */
  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h2 style={styles.title}>Messages</h2>
      </div>

      {convs.length === 0 ? (
        <div style={styles.empty}>
          <IconMessage size={40} color="#ddddee" />
          <p style={styles.emptyText}>Aucun message pour l'instant</p>
          <p style={styles.emptySub}>Vos conversations apparaîtront ici.</p>
        </div>
      ) : (
        <div style={styles.list}>
          {convs.map(c => (
            <ConvItem key={c.id} conv={c} currentUid={user.uid} onClick={() => setActive(c)} />
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  page       : { background: '#f7f8fa', minHeight: '100vh', paddingBottom: 80 },
  header     : { padding: '20px 16px 8px', borderBottom: '1px solid #f0f0f0', background: '#fff' },
  title      : { fontSize: 22, fontWeight: 700, color: '#1a1a1a' },
  list       : { background: '#fff' },
  convItem   : { display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '14px 16px', background: 'none', border: 'none', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', textAlign: 'left' },
  avatar     : { width: 48, height: 48, borderRadius: 24, background: '#f0f0f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  convInfo   : { flex: 1, minWidth: 0 },
  convRow    : { display: 'flex', justifyContent: 'space-between', marginBottom: 2 },
  convName   : { fontSize: 15, fontWeight: 600, color: '#1a1a1a' },
  convTime   : { fontSize: 12, color: '#aaaaaa' },
  convLast   : { fontSize: 13, color: '#888888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' },
  chatWrap   : { display: 'flex', flexDirection: 'column', height: '100vh', background: '#f7f8fa' },
  chatHeader : { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#fff', borderBottom: '1px solid #f0f0f0', flexShrink: 0 },
  backBtn    : { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 4 },
  chatAvatar : { width: 38, height: 38, borderRadius: 19, background: '#f0f0f5', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  chatName   : { fontSize: 16, fontWeight: 700, color: '#1a1a1a' },
  msgList    : { flex: 1, overflowY: 'auto', padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  bubble     : { maxWidth: '72%', padding: '10px 14px', borderRadius: 18 },
  bubbleMine : { background: '#4361ee', alignSelf: 'flex-end', borderBottomRightRadius: 4 },
  bubbleOther: { background: '#fff', alignSelf: 'flex-start', borderBottomLeftRadius: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  bubbleText : { fontSize: 14, lineHeight: 1.4 },
  inputRow   : { display: 'flex', gap: 10, padding: '10px 12px', background: '#fff', borderTop: '1px solid #f0f0f0', flexShrink: 0 },
  msgInput   : { flex: 1, background: '#f5f5f8', border: 'none', borderRadius: 22, padding: '10px 16px', fontSize: 14, outline: 'none', color: '#1a1a1a' },
  sendBtn    : { background: '#4361ee', border: 'none', borderRadius: '50%', width: 42, height: 42, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  spinner    : { width: 32, height: 32, border: '3px solid #eee', borderTopColor: '#4361ee', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  centered   : { background: '#f7f8fa', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px 80px' },
  lockBox    : { background: '#eef0ff', borderRadius: 20, width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 },
  lockTitle  : { color: '#1a1a1a', fontSize: 22, fontWeight: 700, marginBottom: 8 },
  lockSub    : { color: '#888888', fontSize: 14, marginBottom: 32, textAlign: 'center', maxWidth: 280 },
  btnPrimary : { background: '#4361ee', color: '#fff', border: 'none', borderRadius: 14, height: 52, width: '100%', maxWidth: 320, fontSize: 16, fontWeight: 600, cursor: 'pointer' },
  empty      : { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, paddingTop: 80 },
  emptyText  : { color: '#555', fontSize: 16, fontWeight: 600 },
  emptySub   : { color: '#aaaaaa', fontSize: 13 },
};
